import React, { useCallback, useEffect } from 'react';
import {
  Box,
  HStack,
  useRadio,
  useRadioGroup,
} from '@chakra-ui/react';
import { calculateDayDifference } from '#utils/duration';

const GRANULARITY_OPTIONS = [
  { value: 'DAY', label: '1d', days: 1 },
  { value: 'WEEK', label: '1w', days: 7 },
  { value: 'MONTH', label: '1m', days: 30 },
];
const defaultOption = GRANULARITY_OPTIONS[0];

const findOptionByLabel = (label) => GRANULARITY_OPTIONS.find((opt) => opt.label === label);
const findOptionByValue = (value) => GRANULARITY_OPTIONS.find((opt) => opt.value === value);

const RadioOption = ({ label, ...radioProps }) => {
  const { getInputProps, getRadioProps } = useRadio(radioProps);
  const input = getInputProps();
  const checkbox = getRadioProps();

  return (
    <Box as="label" textAlign="center" opacity={radioProps.isDisabled ? 0.4 : 1}>
      <input {...input} />
      <Box
        {...checkbox}
        width="40px"
        padding={2}
        backgroundColor="primary.400"
        cursor={radioProps.isDisabled ? 'not-allowed' : 'pointer'}
        _checked={{ bg: 'primary.200', color: 'white' }}
      >
        {label}
      </Box>
    </Box>
  );
};

export const GranularitySelect = ({ dateRange, granularity, onChange }) => {
  const daysInRange = dateRange?.startDate && dateRange?.endDate
    ? calculateDayDifference(dateRange.endDate, dateRange.startDate)
    : Infinity;

  const handleChange = useCallback((label) => {
    const option = findOptionByLabel(label);
    onChange(option.value);
  }, [onChange]);

  const { getRootProps, getRadioProps } = useRadioGroup({
    name: 'granularity',
    value: findOptionByValue(granularity)?.label || defaultOption.label,
    onChange: handleChange,
  });

  useEffect(() => {
    if (!granularity) {
      handleChange(defaultOption.label);
    }
  }, [granularity, handleChange]);

  useEffect(() => {
    const selectedOption = findOptionByValue(granularity);
    if (selectedOption && selectedOption.days >= daysInRange) {
      handleChange(defaultOption.label);
    }
  }, [daysInRange, handleChange, granularity]);

  return (
    <HStack
      {...getRootProps()}
      columnGap="1px"
      backgroundColor="primary.300"
      border="1px solid"
      borderColor="primary.300"
      borderRadius="md"
      overflow="hidden"
      maxHeight="40px"
    >
      {GRANULARITY_OPTIONS.map((option) => (
        <RadioOption
          key={option.label}
          label={option.label}
          {...getRadioProps({ value: option.label })}
          isDisabled={option.days >= daysInRange}
        />
      ))}
    </HStack>
  );
};
